import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from 'src/app/core/services/AuthService';
import { lastValueFrom } from 'rxjs';
import { PlantillasService } from 'src/app/core/services/plantilla.service';
import { SignatureSizeService } from 'src/app/core/services/signature-size.service';
import { PermisosModalComponent } from 'src/app/shared/components/permisos-acceso/permisos-modal.component';
import { TableAction } from 'src/app/shared/components/table-paginator/table-paginator.component';
import Swal from 'sweetalert2';


interface Item {
    id: number,
    nombre?: string;
    nombre_corto?: string;
    width?: string;
    height?: string;
    createdAt?: string,
    updatedAt?: string,
    deletedAt?: string,
    publica?: boolean,
}

type ImageSource =
    | File
    | {
        buffer: ArrayBuffer | Uint8Array;
        mime?: string;
        name?: string;
    };

@Component({
    selector: 'app-signature-size-mnto',
    templateUrl: './signature-size-mto.component.html',
    styleUrls: ['./signature-size-mto.component.scss']
})
export class SignatureSizeMtoComponent implements OnInit {


    @ViewChild('modalSignatureSize') modalSignatureSize!: TemplateRef<any>;


    currentItems: Item[] = []; // Los datos para la página actual

    currentPage: number = 1;
    pageSize: number = 10; // Tamaño de página inicial
    totalItems: number = 0;
    spinner: boolean;

    customActions: TableAction[] = [];
    isEdition: boolean;

    // SELLO
    selectedFiles: File | File[] | FileList | null = null;
    imagenFirma: string;
    imagenUrl: string;
    scaled: any;
    firmaCustom: boolean = false;
    existeImagen: boolean = false;
    accesUser: boolean = false;

    formSignatureSize: FormGroup;

    tableHeaders = [
        { key: 'nombre', label: 'Nombre' },
        { key: 'nombre_corto', label: 'Nombre Corto' },
        { key: 'width', label: 'Ancho' },
        { key: 'height', label: 'Alto' },
        { key: 'createdAt', label: 'Fecha Creación' },
        { key: 'updatedAt', label: 'Fecha Actualización' }
    ];

    constructor(
        private signatureSizeService: SignatureSizeService,
        private plantillaService: PlantillasService,
        private modalService: NgbModal,
        private authService: AuthService,
        private fb: FormBuilder
    ) {
        this.formSignatureSize = this.fb.group({
            id: new FormControl('', []),
            nombre: new FormControl('', [Validators.required]),
            nombre_corto: new FormControl('', [Validators.required]),
            width: new FormControl('', [Validators.required]),
            height: new FormControl('', [Validators.required]),
            publica: [false]
        })
    }


    async ngOnInit() {
        this.accesUser = this.authService.hasRoleAuth('ADMINISTRADOR');
        this.customActions = [
            {
                label: 'Editar',
                handler: (item: Item) => {
                    this.isEdition = true;
                    this.existeImagen = false;
                    this.cleanSello();
                    const { updatedAt, createdAt, deletedAt, ...itemWithoutAditionalProperties } = item;
                    if (itemWithoutAditionalProperties) {
                        this.formSignatureSize.setValue({
                            ...itemWithoutAditionalProperties
                        });

                        this.openModal(this.modalSignatureSize);
                    }
                },
                icon: 'fa-solid fa-pen-to-square',
                colorClass: 'bg-orange',
                visible: (item: Item) => this.authService.canManageItem(item, this.accesUser)
            },
            {
                label: 'Gestionar Acceso',
                handler: (item: Item) => this.gestionarAcceso(item.id),
                icon: 'fa-solid fa-lock',
                colorClass: 'bg-blue',
                visible: (item: Item) => {
                    return item && this.authService.canManageItem(item, this.accesUser);
                }
            }
        ];

        await this.getSignatureSizes();
    }

    async gestionarAcceso(id: any) {
        const modalRef = this.modalService.open(PermisosModalComponent, {
            centered: true,
            size: 'xl',
        });
        modalRef.componentInstance.id = id;
        modalRef.componentInstance.modeloAcceso = 'SignatureSize';
    }

    async getSignatureSizes() {
        let params = new HttpParams();
        params = params.set('page', this.currentPage).set('limit', this.pageSize).set('pagination', true);
        let sizes = await lastValueFrom(this.signatureSizeService.get<any[]>({ params }));
        const headers: HttpHeaders = sizes.headers;
        this.currentItems = (sizes.body ?? []).map((x: any) => ({
            
                id: x.id, 
                nombre: x.nombre,
                nombre_corto: x.nombre_corto,
                width: x.width,
                height: x.height,
                createdAt: this.formatFechaLocalSinUTC(x.createdAt),
                updatedAt: this.formatFechaLocalSinUTC(x.updatedAt),
                publica: x.publica
        }));
        this.totalItems = Number(headers.get('X-Total-Count'));
    }

    onPageChange(newPage: number): void {
        this.currentPage = newPage;
        this.getSignatureSizes();
    }

    onPageSizeChange(newSize: number): void {
        this.pageSize = newSize;
        this.currentPage = 1; // Siempre vuelve a la primera página al cambiar el tamaño        
        this.getSignatureSizes();
    }

    async action() {

        if (this.formSignatureSize.valid) {
            if (this.isEdition) {
                await this.edit();
            } else {
                await this.save();
            }
        } else {
            Swal.fire(
                '¡Campos requeridos!',
                'Completa todos los campos requeridos',
                'warning'
            )
        }
        await this.getSignatureSizes();
    }

    async save() {
        const { id, ...payload } = this.formSignatureSize.value;
        await lastValueFrom(this.signatureSizeService.create(payload, true));
        Swal.fire(
            '¡Agregado con éxito!',
            'Registro agregado con éxito',
            'success'
        )
        this.modalService.dismissAll();
        this.getSignatureSizes();
    }

    async edit() {
        const { id, ...payload } = this.formSignatureSize.value;
        await lastValueFrom(this.signatureSizeService.update(id, payload, true));

        Swal.fire(
            '¡Actualizado!',
            'Registro actualizado exitosamente',
            'success'
        )
        this.modalService.dismissAll();
    }

    openModal(content: any) {
        this.modalService.open(content, { centered: true, size: 'lg' });
    }


    openModalAdd() {
        this.formSignatureSize.reset();
        this.cleanSello();
        this.isEdition = false;
        this.openModal(this.modalSignatureSize);
    }

    cleanSello() {
        this.existeImagen = false;
        this.scaled = {};
        this.imagenUrl = '';
    }

    async handleFiles(files: any) {
        this.selectedFiles = files;

        const file: File[] =
            this.selectedFiles instanceof File
                ? [this.selectedFiles]
                : Array.isArray(this.selectedFiles)
                    ? this.selectedFiles
                    : this.selectedFiles // FileList
                        ? Array.from(this.selectedFiles as FileList)
                        : [];
        if (file.length > 0) {
            this.existeImagen = true;
            const info: any = await this.loadImageInfo(file[0]);
            this.imagenUrl = info.dataUrl;
        }
    }

    getScaleByImageWidth(width: number): number {

        if (width >= 2600) return 0.15;
        if (width >= 2200) return 0.20;
        if (width >= 1900) return 0.25;
        if (width >= 1600) return 0.30;
        if (width >= 1400) return 0.40;
        if (width >= 1200) return 0.50;
        if (width >= 1000) return 0.60;
        if (width >= 800) return 0.70;
        if (width >= 600) return 0.80;

        return 1;
    }

    getImageSizeCategory(width: number): string {

        if (width >= 2200) return 'extraLarge';
        if (width >= 1400) return 'large';
        if (width >= 800) return 'medium';
        if (width >= 400) return 'small';

        return 'tiny';
    }

    calculateScaledSize(width: number, height: number) {
        const scale = this.getScaleByImageWidth(width).toFixed(2);

        return {
            scale,
            width: Math.round(width * Number(scale)),
            height: Math.round(height * Number(scale)),
        };
    }

    getDisplayMime(mime: string) {
        if (!mime) return "";
        return mime.split("/");
    }

    private guessMimeFromBuffer(u8: Uint8Array): string {
        if (u8[0] === 0x89 && u8[1] === 0x50) return "image/png";
        if (u8[0] === 0xff && u8[1] === 0xd8) return "image/jpeg";
        if (u8[0] === 0x47 && u8[1] === 0x49) return "image/gif";
        return "application/octet-stream";
    }

    loadImageInfo(source: ImageSource) {
        return new Promise((resolve, reject) => {
            let mime = "application/octet-stream";
            let name: string | undefined;
            let size: number | undefined;

            // --------- CASO 1: File ---------
            if (source instanceof File) {
                const file = source;

                mime = file.type || "application/octet-stream";
                name = file.name;
                size = file.size;

                const reader = new FileReader();

                reader.onload = (e: any) => {
                    const dataUrl = e.target.result as string;
                    const img = new Image();

                    img.onload = () => {
                        const width = img.width;
                        const height = img.height;

                        // tu cálculo de escala
                        this.scaled = this.calculateScaledSize(width, height);
                        this.formSignatureSize.patchValue({
                            nombre_corto: this.getImageSizeCategory(this.scaled.width),
                            width: this.scaled.width,
                            height: this.scaled.height,
                        });
                        resolve({
                            width,
                            height,
                            mime,
                            name,
                            size,
                            dataUrl: dataUrl,
                        });
                    };

                    img.onerror = (err) => reject(err);
                    img.src = dataUrl;
                };

                reader.onerror = (err) => reject(err);
                reader.readAsDataURL(file);

                return;
            }

            // --------- CASO 2: Buffer ---------
            let u8: Uint8Array;

            if (source.buffer instanceof Uint8Array) {
                u8 = source.buffer as Uint8Array;
            } else {
                u8 = new Uint8Array(source.buffer as ArrayBuffer);
            }

            mime = source.mime || this.guessMimeFromBuffer(u8);
            name = source.name;
            size = u8.byteLength;

            // Usamos el buffer interno como BlobPart para evitar el error de tipos
            const blob = new Blob([u8.buffer as ArrayBuffer], { type: mime });
            const objectUrl = URL.createObjectURL(blob);

            const img = new Image();
            img.onload = () => {
                const width = img.width;
                const height = img.height;

                // si también quieres escalar cuando viene de buffer:
                this.scaled = this.calculateScaledSize(width, height);
                this.formSignatureSize.patchValue({
                    nombre_corto: this.getImageSizeCategory(this.scaled.width),
                    width: this.scaled.width,
                    height: this.scaled.height,
                });

                const reader = new FileReader();
                reader.onload = (e: any) => {
                    const dataUrl = e.target.result as string;

                    resolve({
                        width,
                        height,
                        mime,
                        name,
                        size,
                        dataUrl,
                    });

                    URL.revokeObjectURL(objectUrl);
                };

                reader.readAsDataURL(blob);
            };

            img.onerror = (err) => {
                URL.revokeObjectURL(objectUrl);
                reject(err);
            };

            img.src = objectUrl;
        });
    }


    /*CONFIGURACION SELLO DIGITAL */
    getStyleString(scaleData: { width: number; height: number }) {
        return `
    width: ${scaleData.width}px;
    height: ${scaleData.height}px;
    border: 2px solid #2196F3;
    border-radius: 6px;
    display: inline-block;
  `;
    }

    getStyleContenedorImg(scaleData: { width: number; height: number }) {
        return `
    width: ${scaleData.width}px;
    height: ${scaleData.height}px;
    text-align: center;
    margin-bottom: 2%;
  `;
    }

    formatFechaLocalSinUTC(iso: string): string {
        // Quitamos la Z y los milisegundos
        const limpia = iso.replace('Z', '').split('.')[0];

        const d = new Date(limpia);

        const pad = (n: number) => n.toString().padStart(2, '0');

        return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} `
            + `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
}
